import { memo, useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'
import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { getFilters, getFilteredProducts, getFeaturedFilters, generateQueryParams, getFiltersFromQuery } from '@dy/commons/utils'
import { HeadTag } from '@dy/commons/components'
import { useMount, useScrollTracker } from '@dy/commons/hooks'
import { getTranslations } from '@/context'
import { designGrid, mq, vw } from '@dy/commons/styles'
import { Layout, HeaderShop, NavShop, SectionFilters, SectionProductList, DynamicSectionFiltersFeatured } from '@/components'
import { useDeviceType } from '@dy/commons/hooks'
import { initializeApollo, GET_CATEGORY, GET_LAYOUT } from '@/api'
import { CATEGORIES, BANNER } from '@dy/commons/utils'

type TypeCatalogPage = {
  data: any,
  layout: any
}

// function categoryPath(category, locale, paths) {
//   const lang = locale.split('-')[0]
//   const params = { slug: category.slug[lang].split('/') }
//   const path = { params, locale }

//   paths.push(path)

//   if(Array.isArray(category?.subcategories)) {
//     for (const subcategory of category.subcategories) {
//       if (subcategory.slug[lang]) paths = categoryPath(subcategory, locale, paths)
//     }
//   }

//   return paths
// }

// function categoriesPaths(categories, lang) {
//   let paths = []

//   for(const category of categories) {
//     if (category?.slug[lang]) paths = categoryPath(category, lang, paths)
//   }

//   return paths
// }


// export async function getStaticPaths() {
//   const locale = NEXT_PUBLIC_LOCALE
//   if (locale === 'en-EU') return { paths: [], fallback: 'blocking' }

//   const apolloClient = initializeApollo()
//   const { data:resData } = await apolloClient.query({
//     query: GET_CATEGORIES,
//     variables: {
//       type: CATEGORIES.PRODUCT
//     }
//   })

//   const paths = resData ? categoriesPaths(resData.categories, locale) : []
//   return { paths, fallback: 'blocking' }
// }

const getSlug = (slug) => typeof slug === 'string' ? slug : slug.join('/')

export async function getServerSideProps({ params, locale }) {
  const { slug } = params
  const dictionary = getTranslations(locale, ['shop', 'product_page', 'home', 'account', 'section_subscribe'])
  const apolloClient = initializeApollo()

  const { data:resData } = await apolloClient.query({
    query: GET_CATEGORY,
    variables: {
      slug: getSlug(slug)
    },
    context: {
      isPrivatePath: false
    }
  })

  const { data:resLayout } = await apolloClient.query({
    query: GET_LAYOUT,
    variables: {
      bannerType: BANNER.SLIM,
      type: CATEGORIES.PRODUCT
    },
    context: {
      isPrivatePath: false
    }
  })

  if (!resLayout || !resData.category) return { notFound: true }

  let modals
  if(resLayout?.modals) modals = resLayout?.modals?.edges?.map(modal => modal.node)

  const arrayOfVariantNodes = Array.isArray(resData.category?.variants?.edges) ? resData.category.variants.edges.map(item => item.node) : []

  return {
    props:{
      data: { ...resData.category, variants: arrayOfVariantNodes },
      translations: dictionary,
      layout: {...resLayout, modals},
      initialApolloState: apolloClient.cache.extract()
    }
  }
}

const updateQueryParams = ({ query, activeFilters, push }) => {
  const slug = getSlug(query.slug)
  const { stringParams} = generateQueryParams(activeFilters)
  push('/catalog/[...slug]', `/catalog/${slug}?${stringParams}`, { shallow: true })
}

let firstLoad = true
const CatalogPage: NextPage<TypeCatalogPage> & { Layout?:FC } = memo(({ data = { variants: [] }, layout }) => {
  const { locale:routerLocale, asPath, query, push, isFallback } = useRouter()
  const lang = useMemo(() => routerLocale.split('-')[0], [routerLocale])
  const isMounted = useMount()
  const { variants, seo, slug } = data
  const { categories } = layout
  const { size } = useDeviceType()

  const [activeFilters, setActiveFilters] = useState({ sort: {recommended: true} })
  const [featuredFilters, setFeaturedFilters] = useState([])

  const filters = useMemo(() => getFilters(variants, lang, false), [variants, lang])

  const [currentProducts, setCurrentProducts] = useState({
    all: Array.from(variants),
    current: Array.from(variants)
  })

  const scrollState = useScrollTracker(160)
  const isScrolling = useMemo(() => scrollState !== 'TOP', [scrollState])

  useEffect(() => {
    if (window.history.scrollRestoration !== 'manual') {
      window.history.scrollRestoration = 'manual'
    }

    return () => {
      window.history.scrollRestoration = 'auto'
    }
  }, [])

  useEffect(() => {
    // @ts-ignore
    if(!firstLoad && query?.slug) {
      //appending gift card to avoid hydratation errors after first load
      setCurrentProducts({
        all: variants,
        current: getFilteredProducts(variants, activeFilters)
      })

      setFeaturedFilters(getFeaturedFilters(activeFilters, filters?.[0]))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, query.slug, size])
  // Note for this useEffect, initially was [isMounted, activeFilters] but
  // since when we update the query params in the next useEffect, this cause a query.slug update
  // (even if the slug remains the same)

  useEffect(() => {
    if(isMounted) {
      if(firstLoad) { firstLoad = false }
      else {
        updateQueryParams({ query, activeFilters, push })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, activeFilters])

  // Get param filters from URL
  useEffect(() => {
    if(filters) {
      const incomingActiveFiltersFromUrl = getFiltersFromQuery(asPath, filters[1])
      //@ts-ignore
      setActiveFilters(incomingActiveFiltersFromUrl)
    }
  }, [filters, asPath])
  if(isFallback) return <></>

  return (
    <>
      <HeadTag seo={seo} pageType='products' slug={slug[lang]} jsonld={{ data, pageType: 'products' }}/>
      <HeaderShop data={categories} />
      <Main>
        <span id='main-top'></span>
        <NavShop categories={categories} />
        <SectionFilters isScrolling={isScrolling} filters={filters?.[0]} activeFilters={activeFilters} setActiveFilters={setActiveFilters} resultsLength={currentProducts?.current?.length} />
        <DynamicSectionFiltersFeatured filters={featuredFilters} />
        <SectionProductList key={`list-products-${asPath}`} products={currentProducts.current} />
        {/* <ViewMore pagination={{productsDisplayed:1, totalProducts:2}} /> */}
      </Main>
    </>
  )
})

CatalogPage.Layout = Layout
export default CatalogPage


const Main = styled.main`
  ${designGrid({ withoutGutter: true })}
  flex-grow: 2;
  margin: 0 auto;
  position: relative;

  #main-top {
    background-color: ${({ theme }) => theme.colors.background};
    display: block;
    height: 2px;
    opacity: .01;
    position: absolute;
    top: -25px;
    user-select: none;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      transform: translateY(-${vw(20, 'desktop')});
    }

    ${mq.greaterThan('desktop')} {
      transform: translateY(-20px);
    }
  }

  ${mq.greaterThan('desktop-wide')} {
    margin: 0 calc((100% - 1650px) / 2);
    max-width: 1650px;
  }

`
